import { useCallback } from 'react'
import {
	EuiCallOut,
	EuiFlexGroup,
	EuiFlexItem,
	EuiLoadingSpinner,
	EuiPanel,
	EuiSpacer,
	EuiText,
	EuiTitle,
} from '@equipmentshare/ds2'

import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'

import type { GridColDef, GridRowParams } from '@mui/x-data-grid-premium'

import DataGrid from 'views/components/DataGrid'

import { AssetTransferStatus } from '../../../graphql'
import { useAssetTransferPolling } from '../TransferOrderDetails.hooks/useAssetTransferPolling'
import config from '../../../config/config'

const ASSET_TRANSFERS_BASE = config.ENVIRONMENT === 'production'
	? 'https://asset-transfers.estrack.com'
	: 'https://staging-asset-transfers.estrack.com'

// Manually defined until inventory-api ships assetTransactions and codegen is re-run.
export interface AssetTransferAsset {
	id: string,
	equipmentMake: { name: string } | null,
	equipmentModel: { name: string } | null,
}

export interface AssetTransfer {
	id: string,
	asset: AssetTransferAsset | null,
	assetTransferOrderId?: string | null,
	assetTransferOrderNumber?: number | null,
}

export interface AssetTransferPanelProps {
	transferOrderId: string,
	assetTransferStatus: AssetTransferStatus,
	assetTransactions: AssetTransfer[],
}

interface AssetInfo {
	id: string,
	transferOrderId: string | null,
	transferOrderNumber: number | null,
}

interface AssetGroup {
	id: string,
	make: string,
	model: string,
	count: number,
	assets: AssetInfo[],
}

function groupAssets( transactions: AssetTransfer[] ): AssetGroup[] {
	const groups = new Map<string, AssetGroup>()
	for ( const tx of transactions ) {
		const make = tx.asset?.equipmentMake?.name ?? '—'
		const model = tx.asset?.equipmentModel?.name ?? '—'
		const key = `${make}__${model}`
		if ( !groups.has( key ) ) {
			groups.set( key, { id: key, make, model, count: 0, assets: [] } )
		}
		const group = groups.get( key )!
		group.count++
		if ( tx.asset ) {
			group.assets.push( {
				id: tx.asset.id,
				transferOrderId: tx.assetTransferOrderId ?? null,
				transferOrderNumber: tx.assetTransferOrderNumber ?? null,
			} )
		}
	}
	return Array.from( groups.values() )
}

function StatusBadge( { status, count }: { status: AssetTransferStatus, count: number } ) {
	switch ( status ) {
		case AssetTransferStatus.Processing:
			return <Chip color="primary" label="Transferring…" />
		case AssetTransferStatus.Completed:
			return (
				<Chip
					color="success"
					label={`Completed · ${count} asset${count === 1 ? '' : 's'}`}
				/>
			)
		case AssetTransferStatus.Failed:
			return <Chip color="error" label="Failed" />
		default:
			return null
	}
}

function AssetGroupDetail( { assets }: { assets: AssetInfo[] } ) {
	return (
		<Box component="ul" sx={{ my: '4px', pl: 3 }}>
			{assets.map( ( asset ) => (
				<Typography key={asset.id} component="li" variant="body2" color="text.secondary">
					Asset {asset.id}
					{asset.transferOrderId != null && asset.transferOrderNumber != null && (
						<>
							{' — '}
							<Link
								href={`${ASSET_TRANSFERS_BASE}/transfer-details/${asset.transferOrderId}`}
								target="_blank"
								rel="noopener noreferrer"
							>
								Transfer #{asset.transferOrderNumber}
							</Link>
						</>
					)}
				</Typography>
			) )}
		</Box>
	)
}

const columns: GridColDef<AssetGroup>[] = [
	{
		field: 'make',
		headerName: 'Make',
		flex: 1,
	},
	{
		field: 'model',
		headerName: 'Model',
		flex: 1,
	},
	{
		field: 'count',
		headerName: 'Qty',
		width: 60,
		align: 'right',
		headerAlign: 'right',
	},
]

export default function AssetTransferPanel( {
	transferOrderId,
	assetTransferStatus,
	assetTransactions,
}: AssetTransferPanelProps ) {
	useAssetTransferPolling( { transferOrderId, assetTransferStatus } )

	const isProcessing = assetTransferStatus === AssetTransferStatus.Processing
	const isFailed = assetTransferStatus === AssetTransferStatus.Failed
	const hasAssets = assetTransactions.length > 0

	const groups = groupAssets( assetTransactions )

	// Both callbacks must be referentially stable — the grid re-renders every row
	// (and drops its expansion state) whenever they change identity.
	const getDetailPanelContent = useCallback(
		( params: GridRowParams<AssetGroup> ) => <AssetGroupDetail assets={params.row.assets} />,
		[],
	)
	const getDetailPanelHeight = useCallback( () => 'auto' as const, [] )

	return (
		<EuiPanel hasBorder paddingSize="m">
			<EuiFlexGroup alignItems="center" gutterSize="m" responsive={false}>
				<EuiFlexItem grow={false}>
					<EuiTitle size="s">
						<h3>Asset Transfers</h3>
					</EuiTitle>
				</EuiFlexItem>
				<EuiFlexItem grow={false}>
					<StatusBadge status={assetTransferStatus} count={assetTransactions.length} />
				</EuiFlexItem>
			</EuiFlexGroup>

			{isFailed && (
				<>
					<EuiSpacer size="m" />
					<EuiCallOut
						color="danger"
						title="Asset transfer did not complete"
						iconType="alert"
					>
						<EuiText size="s">
							<p>
								One or more assets could not be transferred. Please review the transfer order
								and retry or contact support if the issue persists.
							</p>
						</EuiText>
					</EuiCallOut>
				</>
			)}

			<EuiSpacer size="m" />

			{isProcessing && !hasAssets ? (
				<EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
					<EuiFlexItem grow={false}>
						<EuiLoadingSpinner size="m" />
					</EuiFlexItem>
					<EuiFlexItem grow={false}>
						<EuiText size="s" color="subdued">
							<p>Waiting for assets…</p>
						</EuiText>
					</EuiFlexItem>
				</EuiFlexGroup>
			) : (
				<DataGrid
					columns={columns as GridColDef[]}
					rows={groups}
					emptyStateText="No assets transferred."
					getDetailPanelContent={getDetailPanelContent}
					getDetailPanelHeight={getDetailPanelHeight}
				/>
			)}
		</EuiPanel>
	)
}
